import React, { useCallback, useMemo } from 'react';
import { extend } from '@thd-nucleus/data-sources';
import {
  ProductPod,
  ProductImage,
  ProductIdentifier,
  ProductRating,
  PodTapper,
  PodSpacer,
  PodRow,
  ProductBadge,
  ProductSponsored
} from '@thd-olt-component-react/product-pod';
import PropTypes from 'prop-types';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Price } from '@thd-olt-component-react/price';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import {
  useDiscoveryZoneCarouselPodTelemetry,
  useNodeRef,
  useClickInteractionListener
} from '../hooks';

export const DiscoveryZonesPod = (props) => {
  const {
    // eslint-disable-next-line react/prop-types
    itemId,
    storeId,
    position,
    parent,
    telemetryTitle,
    scheme,
    anchorProduct,
    brandTitleMaxLine,
    zoneName,
    zonePosition,
    zoneSponsored,
    podResults,
    hideRatings,
    url,
    product
  } = props;

  // nodes
  const [productPodNodeRef, productPodNode] = useNodeRef();

  const isSponsored = useMemo(() => {
    return product?.info?.isSponsored;
  }, [product?.info?.isSponsored]);

  const showSponsoredLabel = useMemo(() => {
    return !zoneSponsored && isSponsored;
  }, [zoneSponsored, isSponsored]);

  // -----------------
  // Telemetry
  // -----------------
  const {
    triggerClickOnImage,
    triggerClickOnHeader,
    triggerClickOnRating,
    triggerClickOnPod,
    triggerProductPodV7Click
  } = useDiscoveryZoneCarouselPodTelemetry({
    productPodNode: (isSponsored) ? (productPodNode) : (null),
    parent,
    telemetryTitle,
    itemId: product?.itemId,
    trackSource: product?.info?.sponsoredMetadata?.trackSource,
    position,
    slotId: product?.info?.sponsoredMetadata?.slotId
  });

  // Utility function to handle modifier (cmd/ctrl/shift) keys check
  const modifierClick = (event) => {
    return (event.metaKey || event.ctrlKey || event.shiftKey);
  };

  /**
   * Handler on: pod 'click'
   */
  const handleClick = useCallback((event) => {
    if (!isSponsored) return;
    if (modifierClick(event)) return;

    event.preventDefault();
    // --

    // Note: The following trigger call should be removed from this point once the
    //       product-pod tapper gets the same trigger implemented on it, otherwise
    //       the click will be triggered twice.

    // --
    triggerProductPodV7Click(); // analytic & ad-server

    // --
    triggerClickOnPod(); // new-relic
    window.location = url; // redirect

  }, [url, triggerClickOnPod, isSponsored]);

  /**
   * Handler on: image 'click'
   */
  const handleClickOnProductImage = useCallback((event) => {
    if (!isSponsored) return;
    if (modifierClick(event)) return;
    event.preventDefault();
    // --
    triggerClickOnImage(); // new-relic
    window.location = url; // redirect
  }, [url, triggerClickOnImage, isSponsored]);

  /**
   * Handler on: header 'click'
   */
  const handleClickOnProductHeader = useCallback((event) => {
    if (!isSponsored) return;
    if (modifierClick(event)) return;
    event.preventDefault();
    // --
    triggerClickOnHeader(); // new-relic
    window.location = url; // redirect
  }, [url, triggerClickOnHeader, isSponsored]);

  /**
   * Handler on: rating 'click'
   */
  const handleClickOnProductRating = useCallback((event) => {
    if (!isSponsored) return;
    if (modifierClick(event)) return;
    event.preventDefault();
    // --
    triggerClickOnRating(); // new-relic
    window.location = url; // redirect
  }, [url, triggerClickOnRating, isSponsored]);

  // -----------------
  // New-Tab Tracker
  // -----------------
  useClickInteractionListener({
    targetNode: (isSponsored) ? (productPodNode) : (null),
    parent,
    telemetryTitle,
    itemId: product?.itemId,
    sponsoredId: product?.info?.sponsoredMetadata?.sponsoredId,
    trackSource: product?.info?.sponsoredMetadata?.trackSource,
    slotId: product?.info?.sponsoredMetadata?.slotId,
    position
  });

  return (
    <div
      ref={productPodNodeRef}
      data-product-id={itemId}
      data-testid="dz-product-pod"
    >
      <ProductPod
        itemId={itemId}
        storeId={storeId}
        analyticsData={{
          parent, position, scheme, anchorProduct, zoneName, zonePosition, podResults
        }}
        padding="sui-p-2"
        render={(pod) => (
          <>
            <a
              className="sui-top-0 sui-left-0 sui-absolute sui-size-full sui-z-1"
              href={url}
              aria-label="Link"
              onClick={handleClick}
            />
            <PodSpacer minHeight="40px">
              <PodRow noPadding>
                <ProductBadge itemId={pod.itemId} storeId={storeId} />
              </PodRow>
            </PodSpacer>
            <PodTapper zIndex>
              <ProductImage
                itemId={pod.itemId}
                showSecondaryImage={pod.showSecondaryImage}
                onClick={handleClickOnProductImage}
              />
            </PodTapper>

            <PodTapper fitWidth zIndex>
              <ProductHeader
                brand="above"
                itemId={pod.itemId}
                brandTitleMaxLine={brandTitleMaxLine}
                disableShopThisCollection
                productNameLineHeight="tight"
                onClick={handleClickOnProductHeader}
              />
            </PodTapper>

            {!hideRatings
              && (
                <PodTapper fitWidth zIndex>
                  <ProductRating
                    itemId={pod.itemId}
                    onClick={handleClickOnProductRating}
                  />
                </PodTapper>
              )}
            <PodTapper zIndex pointerNone aPointer buttonPointer spanPointer handleClick={handleClick}>
              <Price
                itemId={pod.itemId}
                large={false}
                storeId={storeId}
                displayEachUom={false}
                showPreferredPricingBadge
              />
            </PodTapper>
            {showSponsoredLabel
              && (
                <div className="sui-flex sui-flex-col sui-h-full sui-justify-end">
                  <PodTapper zIndex handleClick={handleClick}>
                    <PodSpacer minHeight="30px">
                      <div className="sui-recs-sponsoredbadge">
                        <ProductSponsored itemId={pod.itemId} position={position} noBackground noPadding />
                      </div>
                    </PodSpacer>
                  </PodTapper>
                </div>
            )}
          </>
        )}
      />
    </div>
  );
};

DiscoveryZonesPod.dataModel = extend(
  ProductPod,
  ProductImage,
  ProductIdentifier,
  ProductHeader,
  SuperSkuMiniSwatches,
  Price,
  ProductRating
);

DiscoveryZonesPod.propTypes = {
  storeId: PropTypes.string,
  position: PropTypes.number,
  parent: PropTypes.string.isRequired,
  telemetryTitle: PropTypes.string.isRequired,
  scheme: PropTypes.string,
  anchorProduct: PropTypes.string,
  brandTitleMaxLine: PropTypes.number,
  zoneName: PropTypes.string,
  zonePosition: PropTypes.string,
  zoneSponsored: PropTypes.bool,
  podResults: PropTypes.number,
  hideRatings: PropTypes.bool,
  url: PropTypes.string.isRequired,
  product: PropTypes.shape({
    itemId: PropTypes.string.isRequired,
    identifiers: PropTypes.shape({
      canonicalUrl: PropTypes.string,
    }),
    info: PropTypes.shape({
      sponsoredBeacon: PropTypes.shape({
        onClickBeacons: PropTypes.arrayOf(PropTypes.string),
        onViewBeacons: PropTypes.arrayOf(PropTypes.string),
      }),
      sponsoredMetadata: PropTypes.shape({
        trackSource: PropTypes.string,
        campaignId: PropTypes.string,
        placementId: PropTypes.string,
        slotId: PropTypes.string,
        sponsoredId: PropTypes.string,
      }),
    }),
  }).isRequired,
};

DiscoveryZonesPod.defaultProps = {
  storeId: '',
  position: '',
  scheme: 'drecs',
  anchorProduct: '',
  brandTitleMaxLine: 4,
  zoneName: '',
  zonePosition: '',
  zoneSponsored: false,
  podResults: 0,
  hideRatings: false
};
DiscoveryZonesPod.displayName = 'DiscoveryZonesPod';
